import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Tab,
  Tabs,
  Button,
  Input,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Divider,
  Progress,
  Switch,
  colors,
} from '@nextui-org/react';
import {
  FaPlus,
  FaEye,
  FaTrash,
  FaCopy,
  FaArrowLeft,
  FaChartBar,
  FaList,
  FaTrashCan,
} from 'react-icons/fa6';
import Icon from '../base/Icon';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { generateGradients, MOTION_KEYS } from '../../util';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  apiKeyService,
  ApiKey as ApiKeyType,
  UsageStats as UsageStatsType,
} from '../../services/apiKeyService';
import TimelineChart from './TimelineChart';
import EndpointBreakdownChart from './EndpointBreakdownChart';
import ModelDistributionChart from './ModelDistributionChart';
import PerformanceTable from './PerformanceTable';
import ErrorAnalysisCard from './ErrorAnalysisCard';
import ExportManager from './ExportManager';
import DateRangeSelector, { DateRange } from './DateRangeSelector';

// Frontend API key type with both backend and frontend formatted fields
type ApiKey = ApiKeyType & {
  key?: string; // For showing the full key during creation
  keyPreview: string;
  createdAt: string;
  lastUsed?: string;
  status: 'active' | 'revoked';
  usageCount: number;
};

type UsageStats = UsageStatsType;

export default function ApiManager() {
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [usageStats, setUsageStats] = useState<UsageStats>({
    total_requests: 0,
    monthly_requests: 0,
    input_tokens: 0,
    output_tokens: 0,
    top_endpoints: [],
  });
  const [usageBreakdown, setUsageBreakdown] = useState<any[]>([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showBreakdownByKey, setShowBreakdownByKey] = useState(false);
  const [chartView, setChartView] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: '',
    endDate: '',
    isAllTime: true, // Default to All Time
  });
  const [allApiKeys, setAllApiKeys] = useState<
    { key_id: string; name: string; is_active: boolean }[]
  >([]);
  const [selectedApiKeyId, setSelectedApiKeyId] = useState<string>('');

  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onOpenChange: onCreateOpenChange,
  } = useDisclosure();
  const {
    isOpen: isViewOpen,
    onOpen: onViewOpen,
    onOpenChange: onViewOpenChange,
  } = useDisclosure();

  // Load real data from API
  useEffect(() => {
    loadApiKeys();
    loadUsageStats();
    loadUsageBreakdown();
    loadAllApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      const keys = await apiKeyService.listApiKeys();
      // Convert backend format to frontend format
      const convertedKeys: ApiKey[] = keys.map((key) => ({
        ...key,
        keyPreview: key.key_preview,
        createdAt: key.created_at,
        lastUsed: key.last_used_at,
        status: key.is_active ? ('active' as const) : ('revoked' as const),
        usageCount: key.usage_count || 0,
      }));
      setApiKeys(convertedKeys);
    } catch (error) {
      console.error('Failed to load API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const loadUsageStats = async () => {
    try {
      const stats = await apiKeyService.getUsageStats();
      setUsageStats(stats);
    } catch (error) {
      console.error('Failed to load usage stats:', error);
      console.error('Error details:', error);

      // Try debug endpoint to understand the issue
      try {
        const debugData = await apiKeyService.debugUsageData();

        // If we have debug data but stats failed, there might be a permission issue
        if (debugData && debugData.api_keys_count > 0) {
          console.warn(
            'API keys exist but stats call failed - possible permission or query issue'
          );
        }
      } catch (debugError) {
        console.error('Debug endpoint also failed:', debugError);
        console.error('This suggests an authentication issue');
      }

      // Keep empty state to show the real issue
      setUsageStats({
        total_requests: 0,
        monthly_requests: 0,
        input_tokens: 0,
        output_tokens: 0,
        top_endpoints: [],
      });
    }
  };

  const loadUsageBreakdown = async () => {
    try {
      const breakdown = await apiKeyService.getUsageBreakdown();
      setUsageBreakdown(breakdown.breakdown || []);
    } catch (error) {
      console.error('Failed to load usage breakdown:', error);
      setUsageBreakdown([]);
    }
  };

  const loadAllApiKeys = async () => {
    try {
      const response = await apiKeyService.listAllApiKeys();
      setAllApiKeys(response.api_keys || []);
    } catch (error) {
      console.error('Failed to load all API keys:', error);
      setAllApiKeys([]);
    }
  };

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    setLoading(true);
    try {
      const newKeyResponse = await apiKeyService.createApiKey({
        name: newKeyName,
      });

      // Convert backend format to frontend format and include the full key for display
      const newKey: ApiKey = {
        ...newKeyResponse,
        key: newKeyResponse.api_key, // Full key only available during creation
        keyPreview: newKeyResponse.key_preview,
        createdAt: newKeyResponse.created_at,
        lastUsed: newKeyResponse.last_used_at,
        status: newKeyResponse.is_active
          ? ('active' as const)
          : ('revoked' as const),
        usageCount: 0,
      };

      setApiKeys((prev) => [...prev, newKey]);
      setSelectedKey(newKey.key || '');
      setNewKeyName('');
      onCreateOpenChange();
      onViewOpen();
      toast.success('API key created successfully!');
    } catch (error) {
      console.error('Failed to create API key:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to create API key'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    if (
      !window.confirm(
        'Are you sure you want to revoke this API key? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      await apiKeyService.revokeApiKey(keyId);

      // Update the local state to reflect the revoked status
      setApiKeys((prev) =>
        prev.map((key) =>
          key.key_id === keyId
            ? { ...key, status: 'revoked' as const, is_active: false }
            : key
        )
      );
      toast.success('API key revoked successfully');
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to revoke API key'
      );
    }
  };

  const handleDeleteKey = async (keyId: string, keyName: string) => {
    if (
      !window.confirm(
        `Are you sure you want to revoke the API key "${keyName}"? This will deactivate the key immediately, but usage data will be preserved for audit purposes.`
      )
    ) {
      return;
    }

    try {
      await apiKeyService.revokeApiKey(keyId);

      // Remove the key from local state
      setApiKeys((prev) => prev.filter((key) => key.key_id !== keyId));
      toast.success('API key revoked successfully');

      // Reload usage stats since they may have changed
      loadUsageStats();
      loadUsageBreakdown();
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to revoke API key'
      );
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Prepare chart data for aggregate view
  const getAggregateChartData = () => {
    if (!usageStats.top_endpoints || usageStats.top_endpoints.length === 0) {
      return [];
    }
    return usageStats.top_endpoints.map((endpoint) => {
      // Clean and shorten endpoint names for better display
      let cleanName = endpoint.endpoint
        .replace('/api/v1/', '')
        .replace('analyze/', '')
        .replace('generate/', '')
        .replace('_', ' ');

      // Truncate long names
      if (cleanName.length > 15) {
        cleanName = cleanName.substring(0, 12) + '...';
      }

      return {
        endpoint: cleanName,
        fullEndpoint: endpoint.endpoint, // Keep full name for tooltip
        requests: endpoint.count,
      };
    });
  };

  // Prepare chart data for breakdown by API key
  const getBreakdownChartData = () => {
    // Use real usage breakdown data from backend
    if (!usageBreakdown || usageBreakdown.length === 0) {
      return [];
    }

    return usageBreakdown;
  };

  const chartData = showBreakdownByKey
    ? getBreakdownChartData()
    : getAggregateChartData();

  // Get colors for API keys (both active and revoked keys for analytics)
  const getApiKeyColors = () => {
    const colors = [
      '#8884d8',
      '#82ca9d',
      '#ffc658',
      '#ff7300',
      '#00ff00',
      '#ff0000',
      '#00ffff',
      '#ff00ff',
    ];
    // Include all keys in analytics, not just active ones
    // Note: Backend analytics endpoints return all keys, while list_api_keys returns only active
    return apiKeys.reduce(
      (acc, key, index) => {
        const keyName = key.name || `Key ${index + 1}`;
        // Add "(revoked)" label for revoked keys (this won't appear in API key list but will in analytics)
        const displayName =
          key.status === 'revoked' ? `${keyName} (revoked)` : keyName;
        // Use muted color for revoked keys
        const color =
          key.status === 'revoked'
            ? `${colors[index % colors.length]}80` // Add 50% opacity
            : colors[index % colors.length];
        acc[displayName] = color;
        return acc;
      },
      {} as { [key: string]: string }
    );
  };

  const apiKeyColors = getApiKeyColors();

  return (
    <motion.div
      variants={{
        [MOTION_KEYS.INITIAL]: {},
        [MOTION_KEYS.ANIMATE]: {
          background: generateGradients([
            colors.red[50],
            colors.yellow[50],
            colors.green[50],
            colors.cyan[50],
            colors.blue[50],
            colors.purple[50],
          ]),
          transition: { duration: 60, repeat: Infinity, ease: 'linear' },
        },
        [MOTION_KEYS.EXIT]: {},
      }}
      initial={MOTION_KEYS.INITIAL}
      animate={MOTION_KEYS.ANIMATE}
      exit={MOTION_KEYS.EXIT}
      className="w-screen h-screen overflow-auto"
    >
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="flat"
              startContent={<Icon icon={FaArrowLeft} className="w-4 h-4" />}
              onPress={() => navigate('/')}
              className="text-gray-600 hover:text-primary"
            >
              Back to Chat
            </Button>
            <div className="h-6 w-px bg-gray-300" />
            <div>
              <h1 className="text-3xl font-bold">API Management</h1>
            </div>
          </div>
          <p className="text-gray-600">
            Manage your API keys and monitor usage for the Dual Dialogue API
          </p>
        </div>

        <Tabs aria-label="API Management Tabs" className="w-full">
          <Tab key="overview" title="Overview">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardBody className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Requests</p>
                        <p className="text-2xl font-bold">
                          {usageStats.total_requests.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Input Tokens</p>
                        <p className="text-2xl font-bold">
                          {usageStats.input_tokens.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Output Tokens</p>
                        <p className="text-2xl font-bold">
                          {usageStats.output_tokens.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Active Keys</p>
                        <p className="text-2xl font-bold">
                          {apiKeys.filter((k) => k.status === 'active').length}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="min-h-[500px]">
                  <CardHeader className="flex justify-between items-center pb-2">
                    <h3 className="text-lg font-semibold">Usage by Endpoint</h3>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          size="sm"
                          isSelected={showBreakdownByKey}
                          onValueChange={setShowBreakdownByKey}
                        />
                        <span className="text-sm text-gray-600">
                          Break down by API key
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          isIconOnly
                          size="sm"
                          variant={chartView ? 'solid' : 'light'}
                          color="primary"
                          onPress={() => setChartView(true)}
                        >
                          <Icon icon={FaChartBar} className="w-4 h-4" />
                        </Button>
                        <Button
                          isIconOnly
                          size="sm"
                          variant={!chartView ? 'solid' : 'light'}
                          color="primary"
                          onPress={() => setChartView(false)}
                        >
                          <Icon icon={FaList} className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody className="pt-2">
                    {chartView ? (
                      <div className="h-96 w-full overflow-hidden">
                        {chartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={chartData}
                              margin={{
                                top: 20,
                                right: 30,
                                left: 20,
                                bottom: 80,
                              }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="endpoint"
                                tick={{ fontSize: 11 }}
                                angle={-45}
                                textAnchor="end"
                                height={70}
                                interval={0}
                              />
                              <YAxis tick={{ fontSize: 11 }} width={50} />

                              <Tooltip
                                contentStyle={{
                                  backgroundColor: 'white',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                }}
                                formatter={(value, name, entry) => {
                                  if (entry?.payload?.fullEndpoint) {
                                    return [value, entry.payload.fullEndpoint];
                                  }
                                  return [value, name ?? ''];
                                }}
                              />

                              {showBreakdownByKey ? (
                                <>
                                  <Legend
                                    wrapperStyle={{ paddingTop: '10px' }}
                                    iconType="rect"
                                  />
                                  {apiKeys.map((apiKey, index) => {
                                    const keyName =
                                      apiKey.name || `Key ${index + 1}`;
                                    // Add "(revoked)" label for revoked keys
                                    const displayName =
                                      apiKey.status === 'revoked'
                                        ? `${keyName} (revoked)`
                                        : keyName;
                                    return (
                                      <Bar
                                        key={displayName}
                                        dataKey={displayName}
                                        fill={apiKeyColors[displayName]}
                                        stackId="endpoints"
                                      />
                                    );
                                  })}
                                </>
                              ) : (
                                <Bar dataKey="requests" fill="#8884d8" />
                              )}
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-gray-500">
                            <Icon
                              icon={FaChartBar}
                              className="w-12 h-12 mb-4 text-gray-300"
                            />
                            <div className="text-center">
                              <p className="text-sm font-medium mb-2">
                                No usage data available
                              </p>
                              <p className="text-xs text-gray-400">
                                {apiKeys.length === 0
                                  ? 'Create an API key and make some API calls to see usage statistics'
                                  : 'Make some API calls to see usage statistics'}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        {usageStats.top_endpoints &&
                        usageStats.top_endpoints.length > 0 ? (
                          usageStats.top_endpoints.map((endpoint, index) => (
                            <div
                              key={endpoint.endpoint}
                              className="flex justify-between items-center mb-3"
                            >
                              <span
                                className="text-sm font-mono truncate flex-1 mr-4"
                                title={endpoint.endpoint}
                              >
                                {endpoint.endpoint}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-500">
                                  {endpoint.count}
                                </span>
                                <div className="w-20">
                                  <Progress
                                    value={Math.max(
                                      5,
                                      (endpoint.count /
                                        usageStats.total_requests) *
                                        100
                                    )}
                                    className="h-2"
                                    color="primary"
                                  />
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                            <Icon
                              icon={FaList}
                              className="w-8 h-8 mb-3 text-gray-300"
                            />
                            <div className="text-center">
                              <p className="text-sm font-medium mb-1">
                                No endpoint usage data
                              </p>
                              <p className="text-xs text-gray-400">
                                API calls will appear here once you start using
                                your API keys
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <h3 className="text-lg font-semibold">Quick Actions</h3>
                  </CardHeader>
                  <CardBody>
                    <div className="space-y-3">
                      <Button
                        color="primary"
                        variant="flat"
                        startContent={
                          <Icon icon={FaPlus} className="w-4 h-4" />
                        }
                        className="w-full justify-start"
                        onPress={onCreateOpen}
                      >
                        Create New API Key
                      </Button>
                      <Button
                        color="default"
                        variant="flat"
                        className="w-full justify-start"
                        onPress={() => window.open('/api/docs', '_blank')}
                      >
                        View API Documentation
                      </Button>
                      <Button
                        color="default"
                        variant="flat"
                        className="w-full justify-start"
                      >
                        Download Usage Report
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </div>
            </div>
          </Tab>

          <Tab key="keys" title="API Keys">
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Your API Keys</h2>
                <Button
                  color="primary"
                  startContent={<Icon icon={FaPlus} className="w-4 h-4" />}
                  onPress={onCreateOpen}
                >
                  Create New Key
                </Button>
              </div>

              <Card>
                <CardBody>
                  <Table aria-label="API Keys table">
                    <TableHeader>
                      <TableColumn>NAME</TableColumn>
                      <TableColumn>KEY</TableColumn>
                      <TableColumn>CREATED</TableColumn>
                      <TableColumn>LAST USED</TableColumn>
                      <TableColumn>USAGE</TableColumn>
                      <TableColumn>STATUS</TableColumn>
                      <TableColumn>ACTIONS</TableColumn>
                    </TableHeader>
                    <TableBody>
                      {apiKeys.map((apiKey) => (
                        <TableRow key={apiKey.id}>
                          <TableCell>{apiKey.name}</TableCell>
                          <TableCell>
                            <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                              {apiKey.keyPreview}
                            </code>
                          </TableCell>
                          <TableCell>{formatDate(apiKey.createdAt)}</TableCell>
                          <TableCell>
                            {apiKey.lastUsed
                              ? formatDate(apiKey.lastUsed)
                              : 'Never'}
                          </TableCell>
                          <TableCell>
                            {apiKey.usageCount.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Chip
                              color={
                                apiKey.status === 'active'
                                  ? 'success'
                                  : 'danger'
                              }
                              variant="flat"
                              size="sm"
                            >
                              {apiKey.status}
                            </Chip>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                isIconOnly
                                size="sm"
                                variant="light"
                                onPress={() => {
                                  setSelectedKey(apiKey.key || '');
                                  onViewOpen();
                                }}
                              >
                                <Icon icon={FaEye} className="w-4 h-4" />
                              </Button>
                              {apiKey.status === 'active' && (
                                <Button
                                  isIconOnly
                                  size="sm"
                                  variant="light"
                                  color="danger"
                                  onPress={() => handleRevokeKey(apiKey.key_id)}
                                  title="Revoke API key"
                                >
                                  <Icon icon={FaTrash} className="w-4 h-4" />
                                </Button>
                              )}
                              {apiKey.status === 'revoked' && (
                                <Button
                                  isIconOnly
                                  size="sm"
                                  variant="light"
                                  color="danger"
                                  onPress={() =>
                                    handleDeleteKey(apiKey.key_id, apiKey.name)
                                  }
                                  title="Revoke API key"
                                >
                                  <Icon icon={FaTrashCan} className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardBody>
              </Card>
            </div>
          </Tab>

          <Tab key="usage" title="Usage Analytics">
            <Card className="h-full">
              <CardHeader>
                <div>
                  <h2 className="text-xl font-semibold">Usage Analytics</h2>
                  <p className="text-gray-600 text-sm mt-1">
                    Comprehensive insights into your API usage patterns,
                    performance, and trends
                  </p>
                </div>
              </CardHeader>
              <CardBody
                className="overflow-y-auto"
                style={{ maxHeight: 'calc(100vh - 280px)' }}
              >
                <div className="space-y-6">
                  {/* Date Range Selector */}
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    <DateRangeSelector
                      className="lg:col-span-1"
                      onDateRangeChange={setDateRange}
                      defaultToCurrentMonth={true}
                      apiKeys={allApiKeys}
                      selectedApiKeyId={selectedApiKeyId}
                      onApiKeyChange={setSelectedApiKeyId}
                    />

                    {/* Quick Stats with Date Range */}
                    <div className="lg:col-span-3">
                      <Card>
                        <CardBody>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                            <div>
                              <p className="text-xs text-gray-500">
                                Date Range
                              </p>
                              <p className="text-sm font-semibold">
                                {dateRange.isAllTime
                                  ? 'All Time'
                                  : dateRange.startDate && dateRange.endDate
                                    ? `${new Date(dateRange.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - ${new Date(dateRange.endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
                                    : 'Current Month'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">
                                Filter Applied
                              </p>
                              <p className="text-sm font-semibold">
                                {dateRange.isAllTime && !selectedApiKeyId
                                  ? 'None'
                                  : !dateRange.isAllTime && selectedApiKeyId
                                    ? 'Date Range & API Key'
                                    : selectedApiKeyId
                                      ? 'API Key'
                                      : 'Date Range'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">
                                Data Source
                              </p>
                              <p className="text-sm font-semibold">Live API</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">
                                Last Updated
                              </p>
                              <p className="text-sm font-semibold">
                                {new Date().toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  </div>

                  {/* Timeline Chart */}
                  <TimelineChart
                    className="col-span-full"
                    dateRange={dateRange}
                    apiKeyId={selectedApiKeyId || undefined}
                  />

                  {/* Endpoint Breakdown Chart */}
                  <EndpointBreakdownChart
                    className="col-span-full"
                    dateRange={dateRange}
                    apiKeyId={selectedApiKeyId || undefined}
                  />

                  {/* Model Distribution and Performance */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ModelDistributionChart
                      dateRange={dateRange}
                      apiKeyId={selectedApiKeyId || undefined}
                    />
                    <ErrorAnalysisCard
                      dateRange={dateRange}
                      apiKeyId={selectedApiKeyId || undefined}
                    />
                  </div>

                  {/* Performance Table */}
                  <PerformanceTable
                    className="col-span-full"
                    dateRange={dateRange}
                    apiKeyId={selectedApiKeyId || undefined}
                  />

                  {/* Export Manager */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                      <Card>
                        <CardHeader>
                          <h3 className="text-lg font-semibold">
                            Analytics Insights
                          </h3>
                        </CardHeader>
                        <CardBody>
                          <div className="space-y-4">
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <h4 className="font-medium text-blue-900 mb-2">
                                Performance Overview
                              </h4>
                              <p className="text-sm text-blue-700">
                                Monitor response times, error rates, and token
                                usage across all your API endpoints. Use the
                                timeline view to identify trends and optimize
                                your integration.
                              </p>
                            </div>

                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                              <h4 className="font-medium text-green-900 mb-2">
                                Model Insights
                              </h4>
                              <p className="text-sm text-green-700">
                                Track which AI models you're using most
                                frequently and compare their performance
                                characteristics to optimize for both quality and
                                cost.
                              </p>
                            </div>

                            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                              <h4 className="font-medium text-purple-900 mb-2">
                                Error Monitoring
                              </h4>
                              <p className="text-sm text-purple-700">
                                Stay informed about API errors and their causes.
                                Use error analysis to improve your integration
                                and reduce failed requests.
                              </p>
                            </div>

                            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                              <h4 className="font-medium text-orange-900 mb-2">
                                Export & Reporting
                              </h4>
                              <p className="text-sm text-orange-700">
                                Generate detailed reports in CSV or PDF format
                                for deeper analysis, compliance requirements, or
                                team collaboration. Date range filters are
                                automatically applied to exports.
                              </p>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>

                    <ExportManager dateRange={dateRange} />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Tab>

          <Tab key="docs" title="Documentation">
            <Card className="h-full">
              <CardHeader>
                <h2 className="text-xl font-semibold">API Documentation</h2>
              </CardHeader>
              <CardBody
                className="overflow-y-auto"
                style={{ maxHeight: 'calc(100vh - 280px)' }}
              >
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">
                      Available Endpoints
                    </h3>
                    <div className="space-y-3">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <code className="text-sm font-mono">
                          POST /api/v1/propose/response
                        </code>
                        <p className="text-sm text-gray-600 mt-2">
                          Generate AI-powered response suggestions using trained
                          models. This endpoint mirrors the functionality
                          available in the web interface, providing
                          context-aware suggestions for mental health
                          conversations.
                        </p>
                        <div className="mt-2 text-xs text-gray-500">
                          <strong>Default Model:</strong> GPT-4o mini trained •{' '}
                          <strong>Response Time:</strong> 2-5 seconds
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <code className="text-sm font-mono">
                          POST /api/v1/propose/response_padi
                        </code>
                        <p className="text-sm text-gray-600 mt-2">
                          PADI Framework analysis for crisis counseling.
                          Provides structured analysis across four therapeutic
                          components: Problem identification, Attempted
                          solutions, Desired outcomes, and Interventions. Each
                          component focuses on a specific aspect of crisis
                          intervention.
                        </p>
                        <div className="mt-2 text-xs text-gray-500">
                          <strong>Default Model:</strong> GPT-4o mini trained •{' '}
                          <strong>Framework Types:</strong> 4 components
                          available
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <code className="text-sm font-mono">
                          POST /api/v1/analyze/traffic_light
                        </code>
                        <p className="text-sm text-gray-600 mt-2">
                          Analyze text content for mental health risk factors
                          including suicidal ideation, self-harm indicators, and
                          crisis situations. Returns risk level classification
                          and confidence scores.
                        </p>
                        <div className="mt-2 text-xs text-gray-500">
                          <strong>Risk Levels:</strong> low, medium, high •{' '}
                          <strong>Response Time:</strong> {'<1'} second
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <code className="text-sm font-mono">
                          POST /api/v1/analyze/emotion
                        </code>
                        <p className="text-sm text-gray-600 mt-2">
                          Multi-dimensional emotional analysis providing scores
                          for seven emotion categories: joy, sadness, anger,
                          fear, surprise, disgust, and neutral. Useful for
                          understanding emotional tone.
                        </p>
                        <div className="mt-2 text-xs text-gray-500">
                          <strong>Emotions:</strong> 7 categories •{' '}
                          <strong>Scores:</strong> 0.0 - 1.0 range
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <code className="text-sm font-mono">
                          POST /api/v1/analyze/sentiment
                        </code>
                        <p className="text-sm text-gray-600 mt-2">
                          Sentiment analysis with detailed confidence scores and
                          probability breakdown. Classifies text as positive,
                          negative, or neutral with supporting metrics.
                        </p>
                        <div className="mt-2 text-xs text-gray-500">
                          <strong>Classifications:</strong> positive, negative,
                          neutral • <strong>Confidence:</strong> Probability
                          scores included
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <code className="text-sm font-mono">
                          POST /api/v1/summarize/conversation
                        </code>
                        <p className="text-sm text-gray-600 mt-2">
                          Generate concise conversation summaries using the same
                          functionality as the web interface. Ideal for creating
                          session notes, handover information, and supervision
                          reports from conversation contexts.
                        </p>
                        <div className="mt-2 text-xs text-gray-500">
                          <strong>Default Model:</strong> GPT-4o mini trained •{' '}
                          <strong>Use Cases:</strong> Session notes, handovers,
                          reports
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <code className="text-sm font-mono">
                          POST /api/v1/empathetic_rewrite
                        </code>
                        <p className="text-sm text-gray-600 mt-2">
                          Transform messages with enhanced empathy and emotional
                          sensitivity. Ideal for improving therapeutic
                          communication, training staff on empathetic responses,
                          and enhancing client-facing messaging with more
                          compassionate tone.
                        </p>
                        <div className="mt-2 text-xs text-gray-500">
                          <strong>Default Model:</strong> GPT-4o •
                          <strong>Response Time:</strong> 1-3 seconds
                        </div>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <code className="text-sm font-mono">
                          POST /api/v1/propose/response_wrap_up
                        </code>
                        <p className="text-sm text-gray-600 mt-2">
                          Generate professional session wrap-ups and closures
                          with clear next steps for clients. Creates structured
                          endings for therapeutic conversations that ensure
                          proper session documentation and client guidance.
                        </p>
                        <div className="mt-2 text-xs text-gray-500">
                          <strong>Default Model:</strong> GPT-4o •
                          <strong>Use Cases:</strong> Session closure, next
                          steps
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <code className="text-sm font-mono">
                          POST /api/v1/propose/response_c3p
                        </code>
                        <p className="text-sm text-gray-600 mt-2">
                          Generate C3P (Crisis Counseling Principles &
                          Practices) guidance for crisis intervention
                          situations. Provides structured crisis counseling
                          guidance based on established crisis intervention
                          principles and best practices.
                        </p>
                        <div className="mt-2 text-xs text-gray-500">
                          <strong>Default Model:</strong> GPT-4o •
                          <strong>Use Cases:</strong> Crisis intervention,
                          training
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <code className="text-sm font-mono">
                          POST /api/v1/chat/direct
                        </code>
                        <p className="text-sm text-gray-600 mt-2">
                          Generate direct AI chat responses with dual
                          conversation history context. Takes both assistant and
                          client conversation history to provide contextual
                          AI-powered responses for direct therapeutic
                          communication.
                        </p>
                        <div className="mt-2 text-xs text-gray-500">
                          <strong>Default Model:</strong> GPT-4o •
                          <strong>Use Cases:</strong> Direct chat, contextual
                          responses
                        </div>
                      </div>
                    </div>
                  </div>

                  <Divider />

                  <div>
                    <h3 className="text-lg font-semibold mb-3">
                      Authentication
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      All API endpoints require authentication using an API key.
                      Include your API key in the Authorization header with
                      Bearer token format:
                    </p>
                    <code className="block p-3 bg-gray-50 rounded text-sm font-mono">
                      Authorization: Bearer dd_sk_your-api-key-here
                    </code>
                    <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
                      <p className="text-sm text-yellow-800">
                        <strong>Security Note:</strong> Keep your API keys
                        secure and never expose them in client-side code. Use
                        environment variables or secure configuration
                        management.
                      </p>
                    </div>
                  </div>

                  <Divider />

                  <div>
                    <h3 className="text-lg font-semibold mb-3">
                      Core Response & Risk Analysis
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Examples for the core endpoints that generate therapeutic
                      responses and assess mental health risks.
                    </p>

                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-sm mb-2">
                          Propose Response
                        </h4>
                        <code className="block p-3 bg-gray-50 rounded text-xs font-mono overflow-x-auto whitespace-pre">
                          {`curl -X POST http://localhost:8000/api/v1/propose/response \\
  -H "Authorization: Bearer dd_sk_your-key-here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "context": "Client says: I've been feeling really overwhelmed at work lately and don't know how to cope with the stress.",
    "model": "optional-model-override"
  }'`}
                        </code>
                        <p className="text-xs text-gray-600 mt-2">
                          <strong>Response:</strong> Returns a therapeutic
                          response suggestion tailored to the client's situation
                        </p>
                      </div>

                      <div>
                        <h4 className="font-medium text-sm mb-2">
                          Risk Analysis
                        </h4>
                        <code className="block p-3 bg-gray-50 rounded text-xs font-mono overflow-x-auto whitespace-pre">
                          {`curl -X POST http://localhost:8000/api/v1/analyze/traffic_light \\
  -H "Authorization: Bearer dd_sk_your-key-here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "context": "Client mentioned feeling hopeless and having thoughts about not wanting to be here anymore.",
    "model": "optional-model-override"
  }'`}
                        </code>
                        <p className="text-xs text-gray-600 mt-2">
                          <strong>Response:</strong> Returns risk assessment
                          with traffic light classification (low, medium, high)
                          and detailed analysis
                        </p>
                      </div>
                    </div>
                  </div>

                  <Divider />

                  <div>
                    <h3 className="text-lg font-semibold mb-3">
                      PADI Framework Components
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      The PADI Framework is a structured approach used in crisis
                      counseling to systematically explore a client's situation.
                      Each component serves a specific therapeutic purpose:
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <h4 className="font-semibold text-sm mb-1">
                          problem_identification
                        </h4>
                        <p className="text-xs text-gray-600">
                          Identifies and explores the core issues, problems, and
                          concerns the client is currently facing. Helps
                          establish the foundation for understanding their
                          situation.
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <h4 className="font-semibold text-sm mb-1">
                          attempted_solutions
                        </h4>
                        <p className="text-xs text-gray-600">
                          Explores what the client has already tried to address
                          their problems. Understanding previous efforts helps
                          avoid repetition and guides new approaches.
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <h4 className="font-semibold text-sm mb-1">
                          desired_outcome
                        </h4>
                        <p className="text-xs text-gray-600">
                          Understands what the client hopes to achieve and how
                          they want their situation to change. Establishes clear
                          goals and expectations for progress.
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <h4 className="font-semibold text-sm mb-1">
                          interventions
                        </h4>
                        <p className="text-xs text-gray-600">
                          Suggests appropriate therapeutic interventions, coping
                          strategies, and next steps based on the identified
                          problems and desired outcomes.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-sm mb-2">
                          Request Format
                        </h4>
                        <code className="block p-3 bg-gray-50 rounded text-xs font-mono overflow-x-auto">
                          {`{
  "context": "Conversation context or client message",
  "framework_type": "problem_identification|attempted_solutions|desired_outcome|interventions",
  "model": "optional-model-override"
}`}
                        </code>
                      </div>

                      <div>
                        <h4 className="font-medium text-sm mb-2">
                          Complete Example
                        </h4>
                        <code className="block p-3 bg-gray-50 rounded text-xs font-mono overflow-x-auto whitespace-pre">
                          {`curl -X POST http://localhost:8000/api/v1/propose/response_padi \\
  -H "Authorization: Bearer dd_sk_your-key-here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "context": "I have been feeling really overwhelmed lately. My job is stressing me out and I cannot seem to find any time for myself.",
    "framework_type": "problem_identification"
  }'`}
                        </code>
                      </div>

                      <div>
                        <h4 className="font-medium text-sm mb-2">
                          Response Format
                        </h4>
                        <code className="block p-3 bg-gray-50 rounded text-xs font-mono overflow-x-auto whitespace-pre">
                          {`{
  "response": "That sounds incredibly overwhelming - feeling caught between a stressful job and not having time for yourself. When you talk about things not changing despite speaking with your supervisor, how has that been affecting you emotionally?",
  "framework_type": "problem_identification",
  "model_used": "ft:gpt-4o-mini-2024-07-18:moht:sos-sft-2a:BBF6ERzt",
  "prompt_type": "generate_problem_identification",
  "metadata": {
    "context_length": 183,
    "response_length": 234,
    "framework_component": "problem_identification"
  }
}`}
                        </code>
                      </div>
                    </div>
                  </div>

                  <Divider />

                  <div>
                    <h3 className="text-lg font-semibold mb-3">
                      Summarize & Supervision Endpoints
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      These endpoints provide the same functionality as the web
                      interface for conversation summarization and therapeutic
                      supervision analysis.
                    </p>

                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-sm mb-2">
                          Summarize Conversation
                        </h4>
                        <code className="block p-3 bg-gray-50 rounded text-xs font-mono overflow-x-auto whitespace-pre">
                          {`curl -X POST http://localhost:8000/api/v1/summarize/conversation \\
  -H "Authorization: Bearer dd_sk_your-key-here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "context": "Client mentioned feeling anxious about work presentation. Discussed breathing techniques and positive self-talk strategies.",
    "model": "optional-model-override"
  }'`}
                        </code>
                        <p className="text-xs text-gray-600 mt-2">
                          <strong>Response:</strong> Returns a concise summary
                          ideal for session notes and handover information
                        </p>
                      </div>

                      <div>
                        <h4 className="font-medium text-sm mb-2">
                          C3P Crisis Counseling Guide
                        </h4>
                        <code className="block p-3 bg-gray-50 rounded text-xs font-mono overflow-x-auto whitespace-pre">
                          {`curl -X POST http://localhost:8000/api/v1/propose/response_wrap_up \\
  -H "Authorization: Bearer dd_sk_your-key-here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "context": "Client is experiencing severe anxiety and panic attacks. They've mentioned feeling like they can't handle the pressure anymore.",
    "model": "gpt-4o"
  }'`}
                        </code>
                        <p className="text-xs text-gray-600 mt-2">
                          <strong>Response:</strong> Returns C3P crisis
                          counseling guidance based on established principles
                          and best practices
                        </p>
                      </div>

                      <div>
                        <h4 className="font-medium text-sm mb-2">
                          Direct Chat Response
                        </h4>
                        <code className="block p-3 bg-gray-50 rounded text-xs font-mono overflow-x-auto whitespace-pre">
                          {`curl -X POST http://localhost:8000/api/v1/chat/direct \\
  -H "Authorization: Bearer dd_sk_your-key-here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "history_assistant": "Therapist: Hello, how are you feeling today?",
    "history_client": "Client: I'm struggling with anxiety about work and feel overwhelmed.",
    "model": "gpt-4o"
  }'`}
                        </code>
                        <p className="text-xs text-gray-600 mt-2">
                          <strong>Response:</strong> Returns contextual AI
                          response based on dual conversation history
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="p-3 bg-gray-50 rounded-lg border">
                          <h4 className="font-semibold text-sm mb-1">ccsr</h4>
                          <p className="text-xs text-gray-600">
                            Crisis Counseling Supervision and Review framework
                            for crisis intervention and risk assessment
                            guidance.
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg border">
                          <h4 className="font-semibold text-sm mb-1">fist</h4>
                          <p className="text-xs text-gray-600">
                            Functional analysis framework for crisis
                            intervention focusing on behavioral patterns and
                            interventions.
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg border">
                          <h4 className="font-semibold text-sm mb-1">tes</h4>
                          <p className="text-xs text-gray-600">
                            Therapeutic Evaluation System for comprehensive
                            session assessment and therapeutic process
                            evaluation.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Divider />

                  <div>
                    <h3 className="text-lg font-semibold mb-3">
                      Rate Limits & Usage
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 bg-gray-50 rounded">
                        <h4 className="font-medium text-sm mb-1">
                          Current Limits
                        </h4>
                        <p className="text-xs text-gray-600">
                          No rate limits currently enforced for approved users.
                          Usage is tracked for monitoring and analytics
                          purposes.
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded">
                        <h4 className="font-medium text-sm mb-1">
                          Best Practices
                        </h4>
                        <p className="text-xs text-gray-600">
                          Implement proper error handling, use appropriate
                          timeouts, and avoid unnecessary parallel requests to
                          the same endpoint.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Divider />

                  <div>
                    <h3 className="text-lg font-semibold mb-3">
                      Error Handling
                    </h3>
                    <div className="space-y-3">
                      <div className="p-3 bg-red-50 rounded border border-red-200">
                        <code className="text-sm font-mono">
                          401 Unauthorized
                        </code>
                        <p className="text-sm text-red-700 mt-1">
                          Invalid or missing API key. Check your Authorization
                          header format.
                        </p>
                      </div>
                      <div className="p-3 bg-orange-50 rounded border border-orange-200">
                        <code className="text-sm font-mono">
                          400 Bad Request
                        </code>
                        <p className="text-sm text-orange-700 mt-1">
                          Invalid request format or missing required parameters.
                        </p>
                      </div>
                      <div className="p-3 bg-red-50 rounded border border-red-200">
                        <code className="text-sm font-mono">
                          500 Internal Server Error
                        </code>
                        <p className="text-sm text-red-700 mt-1">
                          Server error during processing. Response includes
                          error details.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Tab>
        </Tabs>

        {/* Create API Key Modal */}
        <Modal isOpen={isCreateOpen} onOpenChange={onCreateOpenChange}>
          <ModalContent>
            {(onClose) => (
              <>
                <ModalHeader>Create New API Key</ModalHeader>
                <ModalBody>
                  <Input
                    label="Key Name"
                    placeholder="Enter a name for this API key"
                    value={newKeyName}
                    onValueChange={setNewKeyName}
                    description="Give this key a descriptive name (e.g., 'Production', 'Development')"
                  />
                </ModalBody>
                <ModalFooter>
                  <Button variant="light" onPress={onClose}>
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    onPress={handleCreateApiKey}
                    isLoading={loading}
                  >
                    Create Key
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>

        {/* View API Key Modal */}
        <Modal isOpen={isViewOpen} onOpenChange={onViewOpenChange}>
          <ModalContent>
            {(onClose) => (
              <>
                <ModalHeader>API Key</ModalHeader>
                <ModalBody>
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Copy your API key below. For security reasons, you won't
                      be able to see it again.
                    </p>
                    <div className="flex items-center gap-2">
                      <Input value={selectedKey} readOnly className="flex-1" />
                      <Button
                        isIconOnly
                        variant="flat"
                        onPress={() => copyToClipboard(selectedKey)}
                      >
                        <Icon icon={FaCopy} className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-orange-600">
                      ⚠️ Store this key securely. You won't be able to see the
                      full key again.
                    </p>
                  </div>
                </ModalBody>
                <ModalFooter>
                  <Button color="primary" onPress={onClose}>
                    Done
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>
      </div>
    </motion.div>
  );
}
